import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import FactoryIcon from '@mui/icons-material/Factory';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../../services/api';
import { Add, Search } from '@mui/icons-material';

const initialForm = {
  code: '',
  name: '',
  location: '',
  status: 'ACTIVE',
};

const PAGE_SIZE = 10;

export default function CoachMakeMaster() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ open: false, type: 'success', text: '' });

  const showMessage = (text, type = 'success') =>
    setMessage({ open: true, type, text });

  const loadRows = async () => {
    try {
      setLoading(true);
      const response = await api.get('/coach-makes');
      const data = response?.data?.data ?? response?.data ?? [];
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('loadRows:', error);
      showMessage(error?.response?.data?.message || 'Failed to load coach makes', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const filteredRows = useMemo(() => {
    const value = search.trim().toLowerCase();
    if (!value) return rows;

    return rows.filter((item) =>
      [item.code, item.name, item.location, item.status]
        .some((field) => String(field ?? '').toLowerCase().includes(value))
    );
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, page]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    setEditingId(null);
    setForm(initialForm);
    setOpen(true);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      code: item.code || '',
      name: item.name || '',
      location: item.location || '',
      status: item.status || 'ACTIVE',
    });
    setOpen(true);
  };

  const handleClose = () => {
    if (saving) return;
    setOpen(false);
    setEditingId(null);
    setForm(initialForm);
  };

  const handleSubmit = async () => {
    const code = form.code.trim().toUpperCase();
    const name = form.name.trim();
    const location = form.location.trim();

    if (!code) {
      showMessage('Code is required', 'error');
      return;
    }
    if (!name) {
      showMessage('Name is required', 'error');
      return;
    }

    const payload = {
      code,
      name,
      location: location || null,
      status: form.status,
    };

    try {
      setSaving(true);

      if (editingId) {
        await api.put(`/coach-makes/${editingId}`, payload);
        showMessage('Coach make updated successfully');
      } else {
        await api.post('/coach-makes', payload);
        showMessage('Coach make added successfully');
      }

      setOpen(false);
      setEditingId(null);
      setForm(initialForm);
      await loadRows();
    } catch (error) {
      console.error('handleSubmit:', error);
      showMessage(error?.response?.data?.message || 'Failed to save coach make', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await api.delete(`/coach-makes/${deleteId}`);
      showMessage('Coach make deleted successfully');
      setDeleteId(null);
      await loadRows();
    } catch (error) {
      console.error('handleDelete:', error);
      showMessage(error?.response?.data?.message || 'Failed to delete coach make', 'error');
    }
  };

  const renderStatus = (status) => (
    <Chip
      size="small"
      label={status === 'ACTIVE' ? 'Active' : 'Inactive'}
      color={status === 'ACTIVE' ? 'success' : 'default'}
      variant={status === 'ACTIVE' ? 'filled' : 'outlined'}
    />
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, p: { xs: 1, sm: 2 } }}>
      <Card elevation={2} sx={{ width: '100%', minWidth: 0, borderRadius: 2.5 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2.5 } } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 2 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <FactoryIcon color="primary" />
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Coach Make Master
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Coach manufacturing source / make used for workshop records
                </Typography>
              </Box>
            </Stack>

            {/* <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
            >
              Add Coach Make
            </Button> */}
          </Stack>

          {/* <TextField
            fullWidth
            size="small"
            value={search}
            onChange={handleSearch}
            placeholder="Search by code, name, location or status..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          /> */}


  <Box
          sx={{
            p: 2,

            display: "flex",

            gap: 2,

            justifyContent: "space-between",

            alignItems: {
              xs: "stretch",
              sm: "center",
            },

            flexDirection: {
              xs: "column",
              sm: "row",
            },
          }}
        >
          <TextField
            size="small"
            placeholder="Search by code, name, location or status..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: {
                xs: "100%",
                sm: 380,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent={{
              xs: "space-between",
              sm: "flex-end",
            }}
          >
            <Chip label={`${filteredRows.length} Records`} variant="outlined" />

            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAdd}
              disabled={loading}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                px: 2.5,
                whiteSpace: "nowrap",
              }}
            >
              Add Coach Make
            </Button>
          </Stack>
        </Box>



          {isMobile ? (
            <Stack spacing={1.25}>
              {loading ? (
                <Typography align="center" sx={{ py: 4 }} color="text.secondary">
                  Loading...
                </Typography>
              ) : paginatedRows.length === 0 ? (
                <Typography align="center" sx={{ py: 4 }} color="text.secondary">
                  No coach makes found
                </Typography>
              ) : (
                paginatedRows.map((item) => (
                  <Card key={item.id} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={700}>{item.code}</Typography>
                          <Typography variant="body1" sx={{ overflowWrap: 'anywhere' }}>
                            {item.name}
                          </Typography>
                          {item.location && (
                            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                              {item.location}
                            </Typography>
                          )}
                        </Box>
                        {renderStatus(item.status)}
                      </Stack>

                      <Stack direction="row" justifyContent="flex-end" spacing={0.5} sx={{ mt: 1 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" color="primary" onClick={() => handleEdit(item)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setDeleteId(item.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </CardContent>
                  </Card>
                ))
              )}
            </Stack>
          ) : (
            <TableContainer sx={{ width: '100%', overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 700 }}>S.No.</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Coach Make / Manufacturing Unit</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : paginatedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        No coach makes found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRows.map((item, index) => (
                      <TableRow key={item.id} hover>
                        <TableCell>{(page - 1) * PAGE_SIZE + index + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{item.code}</TableCell>
                        <TableCell sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{item.name}</TableCell>
                        <TableCell sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{item.location || '-'}</TableCell>
                        <TableCell>{renderStatus(item.status)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton color="primary" size="small" onClick={() => handleEdit(item)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton color="error" size="small" onClick={() => setDeleteId(item.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Stack alignItems="center" sx={{ mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              size={isMobile ? 'small' : 'medium'}
              showFirstButton={!isMobile}
              showLastButton={!isMobile}
            />
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pr: 6 }}>
          {editingId ? 'Edit Coach Make' : 'Add Coach Make'}
          <IconButton
            onClick={handleClose}
            disabled={saving}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              fullWidth
              label="Code"
              name="code"
              value={form.code}
              onChange={handleChange}
              inputProps={{ maxLength: 20, style: { textTransform: 'uppercase' } }}
              required
            />
            <TextField
              fullWidth
              label="Coach Make / Manufacturing Unit"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              label="Location"
              name="location"
              value={form.location}
              onChange={handleChange}
              placeholder="e.g. Chennai, Tamil Nadu"
            />
            <TextField
              select
              fullWidth
              label="Status"
              name="status"
              value={form.status}
              onChange={handleChange}
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : editingId ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Coach Make?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Are you sure you want to delete this coach make? If it is already used by coach records, the backend will prevent deletion.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={message.open}
        autoHideDuration={3500}
        onClose={() => setMessage((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={message.type} onClose={() => setMessage((prev) => ({ ...prev, open: false }))} variant="filled">
          {message.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
